import React from 'react'
import {
  Badge,
  Box,
  Button,
  Heading,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'

import { SubscriptionWithRecord } from '../types'
import { formatDate } from '../utils/helpers'

interface SubscribedRecordsTableProps {
  data: SubscriptionWithRecord[]
  isLoading: boolean
  error: unknown
}

const SubscribedRecordsTable: React.FC<SubscribedRecordsTableProps> = ({
  data,
  isLoading,
  error,
}) => {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const tableBg = useColorModeValue('white', 'gray.800')

  const handleRowClick = (recordId: number) => {
    navigate(`/record/data/${recordId}`)
  }

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Subscriptions
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
        <Button
          mt={6}
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer borderRadius="md" boxShadow="sm" bg={tableBg}>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Record Name</Th>
              <Th>Type</Th>
              <Th>Owner Wallet</Th>
              <Th>Expires At</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                </Tr>
              ))
            ) : data.length > 0 ? (
              data.map((item) => (
                <Tr
                  key={`${item.record_id}-${item.subscription.id}`}
                  _hover={{ bg: hoverBg, cursor: 'pointer' }}
                  onClick={() => handleRowClick(item.record_id)}
                >
                  <Td fontWeight="medium">{item.record_name}</Td>
                  <Td>{item.record_type}</Td>
                  <Td>{item.owner_wallet}</Td>
                  <Td>{formatDate(item.subscription.expires_at, { precision: 'seconds' })}</Td>
                  <Td>
                    <Badge colorScheme={item.subscription.is_active ? 'green' : 'gray'}>
                      {item.subscription.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </Badge>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={5} textAlign="center" py={6}>
                  <Text color="gray.500">No subscribed records found</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default SubscribedRecordsTable
